import { useEffect, useState, useCallback } from 'react';
import { Download, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { walletApi } from '../utils/api';
import type { RechargeOrder } from '../types/api';

const PAY_TYPE_LABEL: Record<string, string> = {
  stripe: 'Stripe',
  paypal: 'PayPal',
  antom: 'Antom',
  authorizenet: 'Authorize.Net',
  alipay: 'Alipay',
  wechat: 'WeChat',
};

const STATUS_STYLE: Record<number, { label: string; className: string }> = {
  0: { label: 'Pending', className: 'text-amber-400 bg-amber-400/10' },
  1: { label: 'Paid', className: 'text-emerald-400 bg-emerald-400/10' },
  2: { label: 'Refunded', className: 'text-zinc-400 bg-zinc-400/10' },
  3: { label: 'Failed', className: 'text-red-400 bg-red-400/10' },
};

const BillingPage: React.FC = () => {
  const [orders, setOrders] = useState<RechargeOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await walletApi.getOrders(page, filterStatus);
      if (response.code === 1 && response.data) {
        setOrders(Array.isArray(response.data.data) ? response.data.data : []);
        setLastPage(response.data.last_page || 1);
        setTotal(response.data.total || 0);
        setPerPage(response.data.per_page || 10);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setOrders([]);
    }
    setLoading(false);
  }, [page, filterStatus]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // 重置分页当筛选改变
  useEffect(() => {
    setPage(1);
  }, [filterStatus]);

  const filteredOrders = orders.filter((o) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      o.order_sn?.toLowerCase().includes(term) ||
      o.transaction_id?.toLowerCase().includes(term) ||
      o.id?.toString().includes(term) ||
      o.package?.title?.toLowerCase().includes(term)
    );
  });

  const handleExport = () => {
    const headers = ['ID', 'Order SN', 'Package', 'Credits', 'Amount', 'Currency', 'Pay Type', 'Status', 'Paid At'];
    const rows = filteredOrders.map((o) => [
      o.id,
      o.order_sn,
      o.package?.title || '-',
      o.usable,
      o.total_fee,
      o.currency,
      PAY_TYPE_LABEL[o.pay_type] || o.pay_type,
      STATUS_STYLE[o.status]?.label || String(o.status),
      o.paytime ? new Date(o.paytime * 1000).toLocaleString() : '-',
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatAmount = (amount: string, currency: string) => {
    const symbol = currency === 'USD' ? '$' : currency === 'CNY' ? '¥' : '';
    return `${symbol}${amount} ${currency || ''}`.trim();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Billing</h1>
          <p className="text-zinc-400 mt-1">View your recharge order history</p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by order ID, transaction ID or package..."
              className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600 transition-colors"
            />
          </div>
          <div className="flex gap-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-zinc-600 transition-colors"
            >
              <option value="all">All Status</option>
              <option value="1">Paid</option>
              <option value="0">Pending</option>
              <option value="2">Refunded</option>
              <option value="3">Failed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Order SN</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Package</th>
                <th className="text-right text-zinc-400 text-sm font-medium px-6 py-4">Credits</th>
                <th className="text-right text-zinc-400 text-sm font-medium px-6 py-4">Amount</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Pay Method</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Status</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Paid At</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-zinc-400">
                    Loading...
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-zinc-400">
                    No orders found
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => {
                  const status = STATUS_STYLE[order.status] || {
                    label: String(order.status),
                    className: 'text-zinc-400 bg-zinc-400/10',
                  };
                  return (
                    <tr
                      key={order.id}
                      className="border-b border-zinc-800 last:border-0 hover:bg-zinc-800/50 transition-colors"
                    >
                      <td className="px-6 py-4 text-white text-sm font-mono">
                        <div className="truncate max-w-[200px]" title={order.order_sn}>
                          {order.order_sn}
                        </div>
                        <div className="text-zinc-500 text-xs">#{order.id}</div>
                      </td>
                      <td className="px-6 py-4 text-white text-sm">
                        {order.package?.title || <span className="text-zinc-500">Custom</span>}
                      </td>
                      <td className="px-6 py-4 text-right text-emerald-400 text-sm font-medium">
                        +{order.usable}
                      </td>
                      <td className="px-6 py-4 text-right text-white text-sm font-medium">
                        {formatAmount(order.total_fee, order.currency)}
                      </td>
                      <td className="px-6 py-4 text-zinc-300 text-sm">
                        {PAY_TYPE_LABEL[order.pay_type] || order.pay_type}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${status.className}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-zinc-400 text-sm">
                        {order.paytime_text || formatTime(order.paytime)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="text-zinc-400 text-sm">
          {total > 0 ? (
            <>
              Showing {(page - 1) * perPage + 1}-{Math.min(page * perPage, total)} of {total} orders
            </>
          ) : (
            'No orders'
          )}
        </div>
        {lastPage > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="flex items-center gap-1 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
            >
              <ChevronLeft size={16} />
              Prev
            </button>
            <span className="text-zinc-400 text-sm px-2">
              Page {page} / {lastPage}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage || loading}
              className="flex items-center gap-1 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
